import type { ApiResponse, JwtPayload, JwtUser } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import fetchAPI from '@/lib/fetch-api';
import { getRedirectUrl } from '@/lib/get-redirect-url';
import { useAuthStore } from '@/stores/authStore';

export const login = async (email: string, password: string, analyticsDistinctId?: string) => {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password, analyticsDistinctId }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Login failed' }));

    // Handle 2FA case
    if (res.status === 428) {
      return { requires2FA: true, message: errorData.message };
    }

    const error = new Error(errorData.error || 'Login failed') as Error & { statusCode: number };
    error.statusCode = res.status;
    throw error;
  }

  const data = await res.json();

  if (!data.access_token) {
    throw new Error('Invalid response from server');
  }

  const decodedToken = jwtDecode<JwtPayload>(data.access_token);

  const userAccessibleApp = decodedToken.user.new_app_access;

  // Use the redirect function instead of hardcoded URLs or API response
  const fe_app_url = getRedirectUrl(
    decodedToken.accessible_app,
    userAccessibleApp,
    decodedToken.roles
  );

  return {
    fe_app_url,
    requires2FA: false,
    userAccessibleApp,
    accessible_app: decodedToken.accessible_app,
    userId: decodedToken.sub,
  };
};

export const logout = async () => {
  try {
    const res = await fetch('/api/auth/logout', { method: 'POST' });
    const data = await res.json();

    if (!res.ok) {
      console.warn('Logout API call failed:', data.error || 'Logout failed');
    }
  } catch (error) {
    console.warn('Logout failed:', error);
  }
};

// Token refresh lock to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<{ success: boolean; data?: unknown }> | null = null;
let isRefreshing = false;
let lastRefreshTime = 0;
const MIN_REFRESH_INTERVAL = 5000; // Minimum 5 seconds between refreshes

export const refreshToken = async () => {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  // Prevent too frequent refreshes (safety check)
  const now = Date.now();
  const timeSinceLastRefresh = now - lastRefreshTime;
  if (lastRefreshTime > 0 && timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
    // Return success if we have a valid token in store
    const currentToken = useAuthStore.getState().accessToken;
    if (currentToken) {
      return { success: true, data: { access_token: currentToken } };
    }
  }

  // Double-check lock pattern: verify we're not already refreshing
  if (isRefreshing) {
    // Wait a bit and retry
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (refreshPromise) return refreshPromise;
  }

  isRefreshing = true;
  lastRefreshTime = now;

  // Start a new refresh operation
  refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      const data = await res.json();

      if (res.ok && data.access_token) {
        const decodedToken = jwtDecode<JwtPayload>(data.access_token);

        const user: JwtUser = {
          user: {
            id: Number(decodedToken.sub),
            first_name: decodedToken.user.first_name,
            last_name: decodedToken.user.last_name,
            email: decodedToken.user.email,
          },
          permissions: decodedToken.permissions,
          roles: decodedToken.roles,
          organisations: decodedToken.organisations ?? [],
          active_organisation: decodedToken.active_organisation ?? decodedToken.organisations?.[0],
          accessible_app: decodedToken.accessible_app,
          teams: decodedToken.teams,
        };

        useAuthStore.getState().setAccessToken(data.access_token);
        useAuthStore.getState().setAuthenticated(true);
        useAuthStore.getState().setUser(user);

        return { success: true, data };
      }

      return { success: false };
    } catch {
      // Reset lastRefreshTime on error to allow retry
      lastRefreshTime = 0;
      return { success: false };
    } finally {
      // Clear the lock and promise
      // Use setTimeout to ensure all pending requests get the result first
      setTimeout(() => {
        isRefreshing = false;
        refreshPromise = null;
      }, 500);
    }
  })();

  return refreshPromise;
};

export const forgotPassword = async (email: string) => {
  const response = await fetchAPI<{ message: string }>(
    `${process.env.NEXT_PUBLIC_API_URL}/forgot-password`,
    {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    }
  );

  return {
    success: response.success,
    message: response.message,
  };
};

export const resendActivationEmail = async (email: string): Promise<ApiResponse<null>> => {
  const response = await fetchAPI<null>(
    `${process.env.NEXT_PUBLIC_API_URL}/resend-activation-email`,
    {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    }
  );

  return {
    ...response,
    message: response.message || 'Activation link sent successfully',
  };
};

export const resetPassword = async (
  email: string,
  token: string,
  password: string,
  password_confirmation: string
) => {
  const response = await fetchAPI<{ message: string }>(
    `${process.env.NEXT_PUBLIC_API_URL}/reset-password`,
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        token,
        password,
        password_confirmation,
      }),
      skipAuth: true,
    }
  );

  return {
    success: response.success,
    message: response.message,
  };
};

export const clear2FA = async () => {
  try {
    const res = await fetch('/api/auth/2fa/clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error('Failed to clear 2FA state');
    }

    return { success: true };
  } catch (error) {
    console.error('Error clearing 2FA:', error);
    throw error;
  }
};

export const verify2FA = async (code: string) => {
  const res = await fetch('/api/auth/2fa/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: '2FA verification failed' }));
    throw new Error(errorData.error || '2FA verification failed');
  }

  const data = await res.json();

  if (!data.access_token) {
    throw new Error('Invalid response from server');
  }

  const decodedToken = jwtDecode<JwtPayload>(data.access_token);

  return {
    data: decodedToken,
    fe_app_url: data.fe_app_url,
  };
};

export const clearAuthCookies = async () => {
  const res = await fetch('/api/auth/clear-auth-cookies', {
    method: 'POST',
  });

  if (!res.ok) {
    throw new Error('Failed to clear auth cookies');
  }

  return { success: true };
};
